import React, { useState } from "react";
import { Link } from "react-router";
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import SEO from "../components/SEO";
import { API_BASE_URL } from "../config/constants";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import { EXAMPLE_LANGUAGES } from "../utils/apiExamples";
import SecurityNotice from "../components/SecurityNotice";

const ApiDocs = () => {
  /**
   * Transient per-endpoint copy feedback : the shared success-gated
   * hook (see src/client/hooks/useCopyFeedback.js), whose success
   * flag gates the transient check-icon state so a failed copy never
   * shows a false "copied" confirmation. History : the 2026-07-04
   * overhaul (audit item 12) first routed this page's direct
   * navigator.clipboard call through the shared copyToClipboard
   * helper ; the previous local copiedEndpoint string also shared the
   * untracked-setTimeout bug the hook fixes (copying endpoint A and
   * then B one second later let A's still-pending timer wipe B's
   * confirmation early).
   */
  const { copyStatus, handleCopy } = useCopyFeedback();
  const [selectedLanguages, setSelectedLanguages] = useState({});
  // Use production URL for examples
  const baseUrl = API_BASE_URL;

  const getSelectedLanguage = (endpointId) => {
    return selectedLanguages[endpointId] || "curl";
  };

  /**
   * The jump-to control : this page's only navigation.
   *
   * PORTED FROM middleware-01 (operator directive, 2026-08-21), where
   * renderJumpMenu is the sole navigation on the published API
   * documentation. A dropdown scales to any number of endpoints where
   * a row of pills or a contents list would not, and this page has
   * fourteen.
   *
   * The behaviour is theirs exactly : the menu RESETS to its
   * placeholder after each jump, so choosing the same destination
   * twice works ; the target is scrolled smoothly to the top of the
   * viewport ; and it is then FOCUSED with preventScroll, which is
   * what moves a keyboard and screen-reader user to the destination
   * rather than only moving the pixels. Every card carries tabIndex
   * -1 so it can accept that focus without entering the tab order.
   */
  const handleJump = (event) => {
    const id = event.target.value;
    event.target.value = "";
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
  };

  const setSelectedLanguage = (endpointId, language) => {
    setSelectedLanguages((prev) => ({
      ...prev,
      [endpointId]: language,
    }));
  };

  /**
   * The example for one endpoint in one language.
   *
   * PORTED TO THE SHARED BUILDERS (operator directive, 2026-08-21).
   * This used to be an inline switch that assumed every endpoint sends
   * a JSON body - it emitted a Content-Type header and a -d flag
   * regardless, so documenting any GET would have produced a request
   * nobody can run. It also quoted JavaScript and Python strings with
   * apostrophes where middleware-01 uses quotation marks, and omitted
   * the headers block from the Python example entirely.
   *
   * src/client/utils/apiExamples.js reproduces middleware-01's output
   * shapes instead, so a partner reading our documentation and the
   * tracking documentation sees the same example in the same form.
   */
  const getCodeExample = (endpoint, language) => {
    const chosen =
      EXAMPLE_LANGUAGES.find((entry) => entry.id === language) ||
      EXAMPLE_LANGUAGES[0];
    return chosen.build(endpoint, baseUrl);
  };

  const endpoints = [
    {
      id: "csr-generate",
      method: "POST",
      path: "/api/csr/generate",
      title: "Generate CSR",
      description:
        "Generate a Certificate Signing Request (CSR) and private key",
      detailedDescription:
        "This endpoint generates a new Certificate Signing Request (CSR) along with its corresponding private key. The CSR contains information about your organization and common name (domain or e-mail) that will be included in your SSL Certificate. The generated CSR can be submitted to any Certificate Authority (CA) to obtain an SSL Certificate. The private key must be kept secure and will be needed later to install your SSL Certificate on your server.",
      requestBody: {
        commonName: "example.com",
        organization: "Example Inc",
        organizationUnit: "IT Department",
        city: "San Francisco",
        state: "California",
        country: "US",
        email: "admin@example.com",
        keySize: 2048,
        keyType: "RSA",
        digestAlgorithm: "SHA-256",
      },
      responseExample: {
        success: true,
        data: {
          csr: "-----BEGIN CERTIFICATE REQUEST-----\n...\n-----END CERTIFICATE REQUEST-----",
          privateKey:
            "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----",
          commonName: "example.com",
          isWildcard: false,
          keyType: "RSA",
          keySize: 2048,
          digestAlgorithm: "SHA-256",
        },
      },
    },
    {
      id: "csr-decode",
      method: "POST",
      path: "/api/csr/decode",
      title: "Decode CSR",
      description: "Decode and parse a Certificate Signing Request",
      detailedDescription:
        "This endpoint decodes a Certificate Signing Request (CSR) to extract and display all the information contained within it. Use this to verify the contents of a CSR before submitting it to a Certificate Authority, ensuring the Common Name, organization details, and technical specifications are correct. The decoder will show the subject information, public key details, signature algorithm, and any additional attributes or extensions included in the CSR.",
      requestBody: {
        csr: "-----BEGIN CERTIFICATE REQUEST-----\n...\n-----END CERTIFICATE REQUEST-----",
      },
      responseExample: {
        success: true,
        data: {
          valid: true,
          subject: {
            commonName: "example.com",
            organizationName: "Example Inc",
            organizationalUnitName: "IT Department",
            localityName: "San Francisco",
            stateOrProvinceName: "California",
            countryName: "US",
            emailAddress: "admin@example.com",
          },
          publicKey: {
            algorithm: "RSA",
            bits: 2048,
          },
          signatureAlgorithm: "sha256WithRSAEncryption",
        },
      },
    },
    {
      id: "chain-analyze",
      method: "POST",
      path: "/api/chain/analyze",
      title: "Analyze SSL Certificate Chain",
      description:
        "Analyze an SSL Certificate chain and validate the trust path",
      detailedDescription:
        "This endpoint analyzes a complete SSL Certificate chain to validate the trust path from your SSL Certificate to a trusted root Certificate Authority. It will identify each SSL Certificate in the chain (end-entity, intermediate, and root), check expiration dates, validate signatures, and verify the chain of trust. Use this to troubleshoot SSL Certificate installation issues, verify intermediate SSL Certificates are properly configured, or validate SSL Certificate chains before deployment.",
      requestBody: {
        certificate:
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----",
      },
      responseExample: {
        success: true,
        data: {
          certificate: {
            subject: {
              commonName: "example.com",
              organizationName: "Example Inc",
              countryName: "US",
            },
            issuer: {
              commonName: "Example CA",
              organizationName: "Example Certificate Authority",
            },
            notBefore: "2024-01-01T00:00:00.000Z",
            notAfter: "2025-01-01T00:00:00.000Z",
            isExpired: false,
            daysUntilExpiry: 90,
            serialNumber: "01:23:45:67:89:AB:CD:EF",
            signatureAlgorithm: "sha256WithRSAEncryption",
            publicKey: {
              algorithm: "RSA",
              bits: 2048,
            },
            fingerprints: {
              sha256: "ab:cd:ef:...",
              sha1: "12:34:56:...",
            },
            extensions: {
              subjectAltName: [
                { type: "dns", value: "example.com" },
                { type: "dns", value: "www.example.com" },
              ],
              keyUsage: ["Digital Signature", "Key Encipherment"],
              extKeyUsage: [
                {
                  oid: "1.3.6.1.5.5.7.3.1",
                  name: "TLS Web Server Authentication",
                },
                {
                  oid: "1.3.6.1.5.5.7.3.2",
                  name: "TLS Web Client Authentication",
                },
              ],
              basicConstraints: {
                isCA: false,
              },
              subjectKeyIdentifier: "A1:B2:C3:D4:E5:F6:...",
              authorityKeyIdentifier: {
                keyIdentifier: "12:34:56:78:9A:BC:...",
              },
              certificatePolicies: [
                {
                  oid: "2.23.140.1.2.1",
                  name: "Domain Validated (DV)",
                },
              ],
              authorityInfoAccess: {
                ocsp: ["http://ocsp.example.com"],
                caIssuers: ["http://crt.example.com/ca.crt"],
              },
              crlDistributionPoints: ["http://crl.example.com/ca.crl"],
              certificateTransparency: [
                {
                  version: "v1",
                  logId: "D8:09:55:3B:94:4F:...",
                  timestamp: "2024-01-01T12:00:00.000Z",
                },
              ],
            },
          },
          chain: [
            { type: "leaf", info: {} },
            { type: "intermediate", info: {} },
            { type: "root", info: {} },
          ],
          validation: {
            installed: 3,
            notInstalled: 0,
            chainComplete: true,
            problems: [],
            notes: [],
            links: [
              {
                subject: "example.com",
                issuer: "Example Intermediate CA",
                verified: true,
              },
              {
                subject: "Example Intermediate CA",
                issuer: "Example Root CA",
                verified: true,
              },
            ],
            selfSignedRootVerified: true,
          },
        },
      },
    },
    {
      id: "chain-analyze-url",
      method: "POST",
      path: "/api/chain/analyze-url",
      title: "Analyze SSL Certificate Chain from URL",
      description:
        "Fetch and analyze the SSL Certificate chain from a given URL",
      detailedDescription:
        "This endpoint connects to a specified HTTPS URL and retrieves the complete SSL Certificate chain presented by the server. It performs the same comprehensive analysis as the chain analyzer, but fetches the SSL Certificates directly from the live server. This is useful for monitoring SSL Certificate deployments, verifying SSL Certificate installations, checking for SSL Certificate expiration on production servers, and troubleshooting SSL/TLS connection issues. The tool will show you exactly what SSL Certificate chain browsers see when connecting to your site.",
      requestBody: {
        url: "https://example.com",
      },
      responseExample: {
        success: true,
        data: {
          certificate: {
            subject: {
              commonName: "example.com",
              organizationName: "Example Inc",
              countryName: "US",
            },
            issuer: {
              commonName: "Example CA",
              organizationName: "Example Certificate Authority",
            },
            notBefore: "2024-01-01T00:00:00.000Z",
            notAfter: "2025-01-01T00:00:00.000Z",
            isExpired: false,
            daysUntilExpiry: 90,
            serialNumber: "01:23:45:67:89:AB:CD:EF",
            signatureAlgorithm: "sha256WithRSAEncryption",
            publicKey: {
              algorithm: "RSA",
              bits: 2048,
            },
            fingerprints: {
              sha256: "ab:cd:ef:...",
              sha1: "12:34:56:...",
            },
            extensions: {
              subjectAltName: [
                { type: "dns", value: "example.com" },
                { type: "dns", value: "www.example.com" },
              ],
              keyUsage: ["Digital Signature", "Key Encipherment"],
              extKeyUsage: [
                {
                  oid: "1.3.6.1.5.5.7.3.1",
                  name: "TLS Web Server Authentication",
                },
                {
                  oid: "1.3.6.1.5.5.7.3.2",
                  name: "TLS Web Client Authentication",
                },
              ],
              basicConstraints: {
                isCA: false,
              },
              subjectKeyIdentifier: "A1:B2:C3:D4:E5:F6:...",
              authorityKeyIdentifier: {
                keyIdentifier: "12:34:56:78:9A:BC:...",
              },
              certificatePolicies: [
                {
                  oid: "2.23.140.1.2.1",
                  name: "Domain Validated (DV)",
                },
              ],
              authorityInfoAccess: {
                ocsp: ["http://ocsp.example.com"],
                caIssuers: ["http://crt.example.com/ca.crt"],
              },
              crlDistributionPoints: ["http://crl.example.com/ca.crl"],
              certificateTransparency: [
                {
                  version: "v1",
                  logId: "D8:09:55:3B:94:4F:...",
                  timestamp: "2024-01-01T12:00:00.000Z",
                },
              ],
            },
          },
          chain: [
            { type: "leaf", info: {} },
            { type: "intermediate", info: {} },
            { type: "root", info: {} },
          ],
          validation: {
            installed: 3,
            notInstalled: 0,
            chainComplete: true,
            problems: [],
            notes: [],
            links: [
              {
                subject: "example.com",
                issuer: "Example Intermediate CA",
                verified: true,
              },
              {
                subject: "Example Intermediate CA",
                issuer: "Example Root CA",
                verified: true,
              },
            ],
            selfSignedRootVerified: true,
          },
          source: {
            url: "https://example.com",
            hostname: "example.com",
            port: 443,
          },
        },
      },
    },
    {
      id: "match-verify",
      method: "POST",
      path: "/api/match/verify",
      title: "Verify SSL Certificate/Key Match",
      description: "Verify that an SSL Certificate matches a private key",
      detailedDescription:
        "This endpoint verifies that an SSL Certificate and private key form a matching pair by comparing their cryptographic properties. This is essential when installing SSL Certificates to ensure you are using the correct private key. The tool extracts and compares the public key modulus from both the SSL Certificate and private key. A match confirms they are a valid pair. Use this before SSL Certificate installation to prevent configuration errors, verify backup keys match SSL Certificates, or troubleshoot SSL Certificate installation issues.",
      requestBody: {
        certificate:
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----",
        privateKey:
          "-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----",
      },
      responseExample: {
        success: true,
        data: {
          match: true,
          certificate: {
            subject: {
              commonName: "example.com",
              organizationName: "Example Inc",
            },
            // The names this SSL Certificate covers. Carried
            // alongside the subject so a consumer can identify a
            // certificate that names itself only here - an S/MIME
            // certificate puts its address in an rfc822Name and may
            // carry no common name at all.
            extensions: {
              subjectAltName: [
                { type: "dns", value: "example.com" },
                { type: "dns", value: "www.example.com" },
              ],
            },
            serialNumber: "01:23:45:67:89:AB:CD:EF",
            notBefore: "2024-01-01T00:00:00.000Z",
            notAfter: "2025-01-01T00:00:00.000Z",
            isExpired: false,
          },
        },
      },
    },
    {
      id: "pfx-convert",
      method: "POST",
      path: "/api/pfx/convert",
      title: "Convert to PFX",
      description:
        "Convert SSL Certificate and private key to PFX/PKCS12 format",
      detailedDescription:
        "This endpoint converts an SSL Certificate and its private key into PFX (PKCS#12) format, bundling the Certificate, private key, and any included intermediates. You can provide an optional password to encrypt the PFX. Optionally, you may choose the encryption for password-protected PFX files: 3des-sha1 (legacy compatibility, default) or aes256-sha256 (modern). If no password is supplied, the PFX is created without encryption and the encryption option is ignored.",
      requestBody: {
        certificate:
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----",
        privateKey:
          "-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----",
        password: "optional-password",
        encryption: "3des-sha1",
      },
      responseExample: {
        success: true,
        data: {
          pfx: "MIIJKQIBAzCCCO8GCSqGSIb3DQEHAaCCCOAE...",
          filename: "example.com_2024-01-15T14-30-00.pfx",
          certificate: {
            subject: {
              commonName: "example.com",
              organizationName: "Example Inc",
            },
            // The names this SSL Certificate covers. Carried
            // alongside the subject so a consumer can identify a
            // certificate that names itself only here - an S/MIME
            // certificate puts its address in an rfc822Name and may
            // carry no common name at all.
            extensions: {
              subjectAltName: [
                { type: "dns", value: "example.com" },
                { type: "dns", value: "www.example.com" },
              ],
            },
            notBefore: "2024-01-01T00:00:00.000Z",
            notAfter: "2025-01-01T00:00:00.000Z",
            daysUntilExpiry: 90,
          },
          chainLength: 2,
          passwordProtected: true,
          encryption: "3des-sha1",
        },
      },
    },
    {
      // 2026-07-04 tools-wide overhaul (operator-approved), audit items
      // #9 and #17 (docs/TOOLS_CONSISTENCY_AUDIT.md) : this entry
      // previously documented the pre-envelope flat response
      // ({ success, domain, records, summary }) AND the removed dual-CA
      // (Sectigo® + Trustico®) behaviour. The live route
      // (src/server/routes/caa.js) now nests the payload under `data`
      // and CAAService.generateRecords is Sectigo®-only (its CA_CONFIGS
      // table filters unknown CA keys out of the summary), so the
      // request/response examples here mirror what the API can
      // actually produce.
      id: "caa-generate",
      method: "POST",
      path: "/api/caa/generate",
      title: "Generate CAA Records",
      description:
        "Generate CAA (Certificate Authority Authorization) records for Sectigo® SSL Certificates",
      // 2026-07-04 operator consistency directive : every endpoint
      // section heading must carry explanatory text. This entry and
      // caa-validate below were the only two endpoints without a
      // detailedDescription, so their cards rendered a heading with an
      // empty paragraph under it.
      detailedDescription:
        "This endpoint generates CAA (Certificate Authority Authorization) DNS records that authorize Sectigo® to issue SSL Certificates for your domain. You choose which record types to include : issue for standard SSL Certificates, issuewild for Wildcard SSL Certificates, issuemail for S/MIME SSL Certificates, and iodef for violation reporting to an e-mail address. The iodefEmail value must match the standard e-mail format (letters, digits and . _ % + - in the local part ; punycode for internationalized domains) or the request is rejected with a 400 error : the address is composed into a quoted CAA record value, and the accepted character set keeps that composition safe. Strict validation of the iodefEmail field was introduced on 7 August 2026 : e-mail addresses containing quotes, spaces, or non-ASCII domain characters now return a 400 response. Internationalized domains should be submitted in punycode form. The response contains ready-to-publish zone-file lines together with a summary of the generated record set.",
      requestBody: {
        domain: "example.com",
        certificateAuthorities: {
          sectigo: true,
        },
        recordTypes: {
          issue: true,
          issuewild: false,
          issuemail: true,
          iodef: true,
        },
        iodefEmail: "security@example.com",
      },
      responseExample: {
        success: true,
        data: {
          domain: "example.com",
          records: [
            {
              type: "CAA",
              host: "example.com",
              value: '0 issue "sectigo.com"',
              description: "Allows sectigo.com to issue SSL Certificates",
              raw: 'example.com. IN CAA 0 issue "sectigo.com"',
            },
            {
              type: "CAA",
              host: "example.com",
              value: '0 issuemail "sectigo.com"',
              description:
                "Allows sectigo.com to issue S/MIME SSL Certificates",
              raw: 'example.com. IN CAA 0 issuemail "sectigo.com"',
            },
            {
              type: "CAA",
              host: "example.com",
              value: '0 iodef "mailto:security@example.com"',
              description:
                "Incident reporting e-mail for SSL Certificate issues",
              raw: 'example.com. IN CAA 0 iodef "mailto:security@example.com"',
            },
          ],
          summary: {
            totalRecords: 3,
            certificateAuthorities: ["sectigo"],
            recordTypes: ["issue", "issuemail", "iodef"],
          },
        },
      },
    },
    {
      // 2026-07-04 tools-wide overhaul (operator-approved), audit item
      // #9 : the response example previously showed `parsed` at the top
      // level ; the live route (src/server/routes/caa.js) nests it
      // under `data`. The example record was also switched from the
      // dual-CA-era trustico.com issuer to sectigo.com, the only
      // Certificate Authority the product generates records for
      // (audit item #17).
      id: "caa-validate",
      method: "POST",
      path: "/api/caa/validate",
      title: "Validate CAA Record",
      description:
        "Validate the syntax of a CAA record (supports issue, issuewild, issuemail, and iodef tags)",
      // detailedDescription added alongside caa-generate above
      // (2026-07-04 operator consistency directive) : every endpoint
      // card renders this field under its heading.
      detailedDescription:
        "This endpoint validates the syntax of a single CAA record line before you publish it to DNS. It parses the domain, the flags byte, the tag (issue, issuewild, issuemail, or iodef), and the value, then reports whether the record is structurally valid. Use it to catch formatting mistakes such as a wrong tag name or malformed flags before a broken record blocks SSL Certificate issuance for your domain.",
      requestBody: {
        record: 'example.com. IN CAA 0 issuemail "sectigo.com"',
      },
      responseExample: {
        success: true,
        data: {
          parsed: {
            domain: "example.com",
            flags: 0,
            tag: "issuemail",
            value: "sectigo.com",
            isValid: true,
          },
        },
      },
    },
    {
      id: "caa-check",
      method: "POST",
      path: "/api/caa-checker/check",
      title: "Check CAA Records",
      description:
        "Check existing CAA (Certificate Authority Authorization) DNS records for any domain",
      detailedDescription:
        "This endpoint performs a DNS lookup to check what CAA records are currently configured for a domain. CAA records allow domain owners to specify which Certificate Authorities are authorized to issue SSL Certificates for their domain. The endpoint will return all configured CAA records including issue, issuewild, and iodef records, along with a summary of authorized Certificate Authorities. Use this to verify CAA record configuration, troubleshoot SSL Certificate issuance issues, or audit domain security settings. If no CAA records are found, it means any Certificate Authority can issue SSL Certificates for the domain.",
      requestBody: {
        domain: "example.com",
      },
      responseExample: {
        success: true,
        data: {
          domain: "example.com",
          hasRecords: true,
          recordCount: 3,
          records: [
            {
              flag: "issue",
              value: "sectigo.com",
              critical: false,
              parseable: true,
              flags: 0,
            },
            {
              flag: "issue",
              value: "trustico.com",
              critical: false,
              parseable: true,
              flags: 0,
            },
            {
              flag: "iodef",
              value: "mailto:security@example.com",
              critical: false,
              parseable: true,
              flags: 0,
            },
          ],
          summary: {
            issueCount: 2,
            issuewildCount: 0,
            iodefCount: 1,
            unknownCount: 0,
            authorizedCAs: ["sectigo.com", "trustico.com"],
            blocksAllIssuance: false,
          },
          dnsMethod: "DoH",
        },
      },
    },
    {
      id: "dnsstamp-generate",
      method: "POST",
      path: "/api/dnsstamp/generate",
      title: "Generate DNS Stamp",
      description:
        "Generate a DNS stamp for DNS resolvers (Plain DNS, DNSCrypt, DoH, DoT, DoQ, ODoH)",
      detailedDescription:
        "This endpoint generates DNS stamps according to the IETF draft-denis-dns-stamps-00 specification. DNS stamps encode resolver information in a compact, shareable format.\n\nSupported Protocol Types (use the decimal number in your request):\n• 0 - Plain DNS (standard unencrypted DNS)\n• 1 - DNSCrypt (encrypted DNS protocol)\n• 2 - DNS-over-HTTPS (DoH - RFC 8484)\n• 3 - DNS-over-TLS (DoT - RFC 7858)\n• 4 - DNS-over-QUIC (DoQ - RFC 9250)\n• 5 - Oblivious DoH Target (ODoH target server)\n• 129 - DNSCrypt Relay (0x81 - anonymized DNSCrypt relay)\n• 133 - Oblivious DoH Relay (0x85 - ODoH relay server)",
      requestBody: {
        protocol: 2,
        address: "1.1.1.1",
        hostname: "cloudflare-dns.com",
        path: "/dns-query",
        hashes: [
          "d0b243776ea77ee8ca7c7737b10b5eb36e2541d5d620f7682e5c5ec35f01524e",
        ],
        properties: {
          dnssec: true,
          noLog: true,
          noFilter: true,
        },
      },
      // 2026-07-04 tools-wide overhaul (operator-approved), audit item
      // #9 : the live route (src/server/routes/dnsstamp.js /generate
      // handler) nests the payload under `data` and echoes the request
      // back as `parameters` (there is no top-level `protocolName`
      // field on this endpoint - that field only exists on /decode).
      responseExample: {
        success: true,
        data: {
          stamp:
            "sdns://AgcAAAAAAAAABzEuMS4xLjFAZDBiMjQzNzc2ZWE3N2VlOGNhN2M3NzM3YjEwYjVlYjM2ZTI1NDFkNWQ2MjBmNzY4MmU1YzVlYzM1ZjAxNTI0ZRJjbG91ZGZsYXJlLWRucy5jb20KL2Rucy1xdWVyeQ",
          protocol: 2,
          parameters: {
            protocol: 2,
            address: "1.1.1.1",
            hostname: "cloudflare-dns.com",
            path: "/dns-query",
            hashes: [
              "d0b243776ea77ee8ca7c7737b10b5eb36e2541d5d620f7682e5c5ec35f01524e",
            ],
            properties: {
              dnssec: true,
              noLog: true,
              noFilter: true,
            },
          },
        },
      },
    },
    {
      id: "dnsstamp-decode",
      method: "POST",
      path: "/api/dnsstamp/decode",
      title: "Decode DNS Stamp",
      description: "Decode a DNS stamp to extract resolver configuration",
      detailedDescription:
        "This endpoint decodes DNS stamps according to the IETF draft-denis-dns-stamps-00 specification. It extracts all configuration parameters from the stamp including protocol type, server address, hostname, path, public keys, hashes, and resolver properties.\n\nProtocol Types in Response:\n• 0x00 (0) - Plain DNS\n• 0x01 (1) - DNSCrypt\n• 0x02 (2) - DNS-over-HTTPS (DoH)\n• 0x03 (3) - DNS-over-TLS (DoT)\n• 0x04 (4) - DNS-over-QUIC (DoQ)\n• 0x05 (5) - Oblivious DoH Target\n• 0x81 (129) - DNSCrypt Relay\n• 0x85 (133) - Oblivious DoH Relay",
      requestBody: {
        stamp:
          "sdns://AgcAAAAAAAAABzEuMS4xLjFAZDBiMjQzNzc2ZWE3N2VlOGNhN2M3NzM3YjEwYjVlYjM2ZTI1NDFkNWQ2MjBmNzY4MmU1YzVlYzM1ZjAxNTI0ZRJjbG91ZGZsYXJlLWRucy5jb20KL2Rucy1xdWVyeQ",
      },
      // 2026-07-04 tools-wide overhaul (operator-approved), audit item
      // #9 : the live route (src/server/routes/dnsstamp.js /decode
      // handler) nests `decoded` under `data`.
      responseExample: {
        success: true,
        data: {
          decoded: {
            protocol: "0x02",
            protocolName: "DNS-over-HTTPS",
            address: "1.1.1.1",
            hostname: "cloudflare-dns.com",
            path: "/dns-query",
            hashes: [
              "D0B243776EA77EE8CA7C7737B10B5EB36E2541D5D620F7682E5C5EC35F01524E",
            ],
            properties: {
              dnssec: true,
              noLog: true,
              noFilter: true,
            },
          },
        },
      },
    },
    {
      // THE ONE GET, AND THE ONE BODYLESS ENDPOINT (operator
      // instruction, 2026-08-21 : "The dnsstamp missing docs should be
      // updated and reflect the tool"). It has been registered and
      // undocumented ; documenting it is also what finally exercises
      // the bodyless path the example builders were written for, so the
      // curl tab carries no -d flag, the JavaScript tab reduces to a
      // one-argument fetch, the Python tab omits the headers block, and
      // the Request Body panel does not render at all.
      id: "dnsstamp-protocols",
      method: "GET",
      path: "/api/dnsstamp/protocols",
      title: "List DNS Stamp Protocols",
      description:
        "List every protocol a DNS Stamp can describe, with its identifier and default port",
      detailedDescription:
        "This endpoint returns the protocol types the DNS Stamp Generator and Decoder understand, each with the single-byte identifier that opens a stamp, a plain description, and the port that protocol uses when a stamp does not name one. Use it to populate a protocol chooser in your own interface, or to resolve an identifier you have decoded from a stamp into a name a person can read. It takes no request body and no parameters, so the list it returns is always the complete one.",
      responseExample: {
        success: true,
        data: {
          protocols: [
            {
              value: "0x00",
              name: "Plain DNS",
              description: "Standard DNS without encryption",
              defaultPort: 53,
            },
            {
              value: "0x01",
              name: "DNSCrypt",
              description: "Encrypted DNS using DNSCrypt protocol",
              defaultPort: 443,
            },
            {
              value: "0x02",
              name: "DNS-over-HTTPS",
              description: "DNS over HTTPS (RFC 8484)",
              defaultPort: 443,
            },
            {
              value: "0x03",
              name: "DNS-over-TLS",
              description: "DNS over TLS (RFC 7858)",
              defaultPort: 853,
            },
            {
              value: "0x04",
              name: "DNS-over-QUIC",
              description: "DNS over QUIC (RFC 9250)",
              defaultPort: 853,
            },
            {
              value: "0x05",
              name: "Oblivious DoH Target",
              description: "Oblivious DNS-over-HTTPS target server",
              defaultPort: 443,
            },
            {
              // The anonymized DNSCrypt relay is the ONE entry with no
              // default port : the stamp it relays for supplies the
              // port. The Oblivious relay below it does carry 443, so
              // this is not a property of relays as a class.
              value: "0x81",
              name: "Anonymized DNSCrypt Relay",
              description: "Relay for anonymized DNSCrypt",
              defaultPort: null,
            },
            {
              value: "0x85",
              name: "Oblivious DoH Relay",
              description: "Relay for Oblivious DNS-over-HTTPS",
              defaultPort: 443,
            },
          ],
        },
      },
    },
    {
      id: "dcv-check",
      // Verified against server.js : app.use("/api/dcv", dcvLimiter)
      // adds a SECOND limiter on top of the site-wide one, so this
      // endpoint runs out at 30 rather than 100.
      // A SEPARATE allowance, not a smaller share of the same one :
      // the global limiter's skip() explicitly passes over /dcv/ so the
      // two are not double-counted, which is the more useful fact and
      // the one a reader could not previously get.
      rateLimitNote:
        "Requests are limited to 30 per hour from one IP address, because each check performs live Domain Name System (DNS) or HTTP lookups. This allowance is separate from the 100 per hour the other endpoints share.",
      method: "POST",
      path: "/api/dcv/check",
      title: "Check Domain Control Validation",
      description:
        "Verify domain control validation records (CNAME, TXT, HTTP, HTTPS) for SSL Certificate issuance",
      detailedDescription:
        'This endpoint checks whether domain control validation records or files are properly configured for SSL Certificate issuance. Certificate Authorities require domain owners to prove control over their domain by placing specific CNAME or TXT DNS records, or by hosting a file at a specific HTTP/HTTPS path.\n\nSupported validation methods:\n• cname - Check CNAME DNS record (case-insensitive matching)\n• txt - Check TXT DNS record (case-insensitive matching)\n• http - Check file via HTTP at /.well-known/pki-validation/ (case-sensitive matching)\n• https - Check file via HTTPS at /.well-known/pki-validation/ (case-sensitive matching)\n\nIMPORTANT : DNS-based methods (CNAME, TXT) perform case-insensitive comparison of values. HTTP/HTTPS file-based methods perform case-sensitive comparison - the file content must match exactly as provided by your CA.\n\nThe recordName field is optional for DNS methods (defaults to _dnsauth) but required for HTTP/HTTPS methods (specifies the file name). The dnsMethod field indicates the resolution method used : "DoH" for DNS-over-HTTPS, "native" for system DNS, or null for HTTP/HTTPS file checks.\n\nThe foundValue field (singular) contains the primary result : for CNAME it is the first record, for TXT it is the matching record (or first record if no match), and for HTTP/HTTPS it is raw file content (truncated to 200 chars for display, though the full content is used for comparison). When a TXT check finds multiple records, an additional foundValues field (plural, array) is included containing all records found, so API consumers can see every record that was checked. The foundValues array is only present when more than one TXT record exists.',
      requestBody: {
        domain: "example.com",
        method: "cname",
        expectedValue: "abc123.comodoca.com",
        recordName: "_dnsauth",
      },
      responseExample: {
        success: true,
        data: {
          domain: "example.com",
          method: "cname",
          isValid: true,
          expectedValue: "abc123.comodoca.com",
          foundValue: "abc123.comodoca.com",
          recordName: "_dnsauth.example.com",
          dnsMethod: "DoH",
          checkedAt: "2026-02-24T12:00:00Z",
          details:
            "A CNAME record was found and it matches the expected value.",
        },
      },
    },
    {
      id: "domain-validate",
      method: "POST",
      path: "/api/domain/validate",
      title: "Validate Domain",
      description:
        "Check whether a domain has a real, publicly-registrable Top-Level Domain (TLD)",
      detailedDescription:
        'This endpoint reports whether a domain name uses a real, publicly-registrable Top-Level Domain (TLD) from the Public Suffix List (PSL). It returns the parsed structure (the registrable base domain, the public suffix, and whether a wildcard prefix was supplied) along with a boolean verdict. Unlike the other tools, this endpoint never rejects an invalid domain with an error : it always returns 200 and reports the verdict in the response body, so it can be used as a lookup. It is the same realness check every Trustico® SSL tool applies internally. A wildcard prefix ("*.") is accepted and reported separately.',
      requestBody: {
        domain: "example.com",
      },
      responseExample: {
        success: true,
        data: {
          input: "example.com",
          normalized: "example.com",
          isWildcard: false,
          baseDomain: "example.com",
          publicSuffix: "com",
          isValid: true,
        },
      },
    },
  ];

  // Named and ordered by the shared module, which follows
  // middleware-01 : "curl" lower-case, as the tool writes itself.
  const languages = EXAMPLE_LANGUAGES;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : canonicalUrl
          added so search engines index the docs at their one true address.

          This page must NOT be routed under /api. Everything under that
          prefix is answered by server.js's unmatched-endpoint handler,
          which is registered ahead of the static and SPA handlers, so a
          path there serves a JSON 404 to every direct visit, refresh,
          bookmark and crawler - reachable only by in-app navigation,
          which makes the breakage invisible from inside the site. */}
      <SEO
        title="API Documentation - SSL Tools API Reference"
        description="Complete API documentation for SSL Tools. Generate CSRs, decode SSL Certificates, analyze SSL chains, and convert to PFX format using our REST API."
        keywords="SSL API, SSL Certificate API, CSR API, SSL tools API documentation, REST API, SSL Certificate chain API, PFX converter API"
        canonicalUrl="https://tools.trustico.com/api-docs"
      />

      {/*
        THE STICKY BAR IS THIS PAGE'S ONE PIECE OF CHROME, ported from
        middleware-01 (operator directive, 2026-08-21 : "These tools
        should reflect what we have done there ... Consistency is
        important"). It carries the page name and the jump-to control,
        which is the only navigation either page has : fourteen
        endpoints is more than a pill row or a contents list can carry
        without becoming the page.

        Sections are listed before endpoints and the closing sections
        after them, in the order they appear, so the menu reads as the
        page reads.
      */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 mb-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-4">
          {/* THE STICKY BAR CARRIES THE PAGE'S ONLY TITLE, exactly as
              middleware-01's does (operator ruling, 2026-08-21). The
              large heading below it said the same thing in different
              words, and of the two the small always-visible one earns
              its place : it is still on screen when a reader is forty
              endpoints down the page. It is the h1, so the page still
              has exactly one. */}
          <h1 className="font-semibold text-heading truncate">
            Trustico® Tools API
          </h1>
          <select
            id="jump-menu"
            aria-label="API Endpoint Quick Access"
            onChange={handleJump}
            defaultValue=""
            className="shrink-0 rounded-lg border border-control bg-white dark:bg-gray-800 text-heading px-3 py-2 max-w-[60%] sm:max-w-xs"
          >
            <option value="">API Endpoint Quick Access</option>
            <option value="endpoint-information">
              API Endpoint Information
            </option>
            {endpoints.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.method} - {endpoint.title}
              </option>
            ))}
            <option value="error-responses">Error Responses</option>
            <option value="integration-guide">Integration Guide</option>
          </select>
        </div>
      </div>

      {/* OUTSIDE the sticky bar. Placed inside it, the notice pinned its
          title and seventy words to the top of the viewport for the whole
          of a page thousands of pixels long, and grew the bar from a title
          row to roughly 230px - which silently broke every jump-menu
          target, because scroll-mt-24 was sized for the short bar and the
          endpoint head rows landed back underneath it. That is the same
          hidden-heading regression the scroll-mt was added for. */}
      <SecurityNotice />

      <div className="mb-8">
        <p className="text-body mb-3">
          Integrate SSL tools into your applications with our comprehensive REST
          API. Generate Certificate Signing Requests, decode and analyze SSL
          Certificates, validate SSL Certificate chains, and convert SSL
          Certificates to various formats programmatically. Our API provides
          simple JSON-based endpoints that make it easy to automate SSL
          Certificate management tasks and build SSL Certificate functionality
          into your own applications.
        </p>

        <p className="text-body mb-6">
          Looking for a no-code solution? If you need a quick integration
          without writing server-side code, check out our CSR Popup tool. It can
          be embedded in any website with just a few lines of HTML and
          JavaScript. Explore{" "}
          {/* 2026-07-04 operator consistency directive : "More
              Information" is generic link text, which the Trustico®
              style guide forbids ; the link text now names the
              destination page. */}
          <Link
            to="/csr-integration"
            className="text-link hover:text-link-hover font-medium"
          >
            CSR Popup Integration Examples
          </Link>{" "}
          🔗
        </p>
      </div>

      <div
        id="endpoint-information"
        tabIndex={-1}
        className="scroll-mt-24 mb-8 bg-linear-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 p-8 rounded-xl shadow-xs"
      >
        {/* 2026-07-04 operator consistency directive : all section
            headings on this page recased from h2 text-xl to the
            reference section heading size (text-lg font-semibold),
            and every section heading now carries an explanatory
            paragraph. The smaller in-card labels (Endpoint / Request
            Body / Response Example / Code Example and the Integration
            Guide topics) become sub-headings so no section heading deviates
            from the reference size. */}
        <div className="flex items-center space-x-3 mb-2">
          <CodeBracketIcon className="w-6 h-6 text-link" />
          <h2 className="text-lg font-semibold text-heading">
            API Endpoint Information
          </h2>
        </div>
        <p className="text-body mb-4">
          These are the connection basics every request shares : the base URL,
          the JSON format, the rate limit, and the authentication policy.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="font-medium text-muted mb-1">Base URL</p>
              <code className="font-mono text-heading">{baseUrl}/api</code>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="font-medium text-muted mb-1">Format</p>
              <p className="text-heading">JSON Request / Response</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="font-medium text-muted mb-1">Rate Limit</p>
              <p className="text-heading">
                {/* PER HOUR, NOT PER MINUTE (2026-08-21). server.js
                    builds the shared limiter with windowMs = 60 * 60 *
                    1000 and max = 100, so this overstated the allowance
                    sixtyfold : a partner sizing a batch job against it
                    would have been refused after the first hundred
                    requests of the hour with no idea why. Found when
                    the per-endpoint access notes added below stated the
                    real figure and disagreed with this one. */}
                100 Requests Per Hour
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="font-medium text-muted mb-1">Authentication</p>
              <p className="text-heading">None</p>
            </div>
          </div>
        </div>
      </div>

      {/* API Endpoints */}
      <div className="space-y-8">
        {endpoints.map((endpoint) => (
          <div
            key={endpoint.id}
            id={endpoint.id}
            /* SCROLL MARGIN FOR THE STICKY BAR (2026-08-21 review).
               scrollIntoView has no offset, and block:"start" puts the
               target's top edge at the top of the viewport - which is
               exactly the strip the bar is painted over, so every jump
               hid the head row this change exists to put there. */
            className="scroll-mt-24 card p-6"
            /* Focusable by the jump menu WITHOUT entering the tab
               order, exactly as middleware-01's cards are. Scrolling
               alone moves the pixels ; focus is what moves a keyboard
               or screen-reader user with them. */
            tabIndex={-1}
          >
            {/*
              THE CARD HEAD CARRIES METHOD, PATH AND ACCESS TOGETHER,
              ported from middleware-01's renderBadges (operator
              directive, 2026-08-21). The method badge used to sit
              alone on the right of the title while the path lived in a
              separate block below, so the three facts a reader scans
              for were in three places. Public is stated rather than
              assumed : every Trustico® tools endpoint is open, and a
              reader coming from the tracking documentation - where
              some endpoints need a key - has no way to know that
              unless it is written.
            */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span
                className={`px-3 py-1 rounded-lg font-medium ${
                  endpoint.method === "POST"
                    ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                    : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
                }`}
              >
                {endpoint.method}
              </span>
              <code className="text-heading break-all">{endpoint.path}</code>
              <span className="px-3 py-1 rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                Public
              </span>
            </div>
            {/* Endpoint section heading at the reference size (see
                the API Endpoint Information comment above). */}
            <h2 className="text-lg font-semibold text-heading mb-2">
              {endpoint.title}
            </h2>
            <p className="text-body leading-relaxed mb-3">
              {endpoint.detailedDescription}
            </p>
            {/* The access note : what a caller has to do to use this
                endpoint, and what will stop them. middleware-01 states
                this per endpoint rather than once at the top, because
                a reader who arrives at a card from the jump menu never
                sees the top. */}
            <p className="text-body mb-4">
              <strong>Access :</strong> No authentication is required.{" "}
              {/* SHARED, NOT PER ENDPOINT (2026-08-21 review). The
                  global limiter keys on the client IP alone -
                  limiterKey in server.js reads getTrustedClientIp and
                  nothing else, and the store prefix carries no path
                  segment - so one hundred an hour is the TOTAL across
                  every endpoint it covers. Printed once per card
                  without that word, it reads as a property of the card
                  it sits on : a partner planning sixty CSR generations
                  and sixty chain analyses would read two cards each
                  saying they are inside the limit and be refused on the
                  hundred and first request of the two combined. The
                  same shape of defect as the per-minute figure this
                  page carried, stated twelve times instead of once. */}
              {endpoint.rateLimitNote ||
                "Requests are limited to 100 per hour from one IP address, shared across every endpoint on this page except Domain Control Validation checks."}
            </p>

            {/* Endpoint URL */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-heading mb-2">
                Endpoint
              </h3>
              <div className="flex items-center bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg">
                {/* THE FULL URL, not the path : the head above
                    already carries the path, and this is the value the
                    button beside it copies. Showing the path here made
                    the two disagree with each other. */}
                <code className="flex-1 break-all text-heading">
                  {baseUrl}
                  {endpoint.path}
                </code>
                {/* A LABELLED BUTTON, NOT AN ICON (operator directive,
                    2026-08-21). middleware-01's published API
                    documentation labels every copy control "Copy" and
                    swaps it to "Copied" for two seconds - the same
                    window this page's shared hook already uses. The
                    word carries the affordance and the confirmation to
                    everyone at once, where the icon form needed a
                    title and an aria-label to say in assistive
                    technology what the picture said on screen. */}
                <button
                  type="button"
                  onClick={() =>
                    handleCopy(`${baseUrl}${endpoint.path}`, endpoint.id)
                  }
                  className="ml-2 shrink-0 rounded-md border border-control px-3 py-1 text-body hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  {copyStatus[endpoint.id] ? "Copied" : "Copy"}
                </button>
              </div>
            </div>

            {/* Request Body : ONLY WHEN THERE IS ONE (2026-08-21
                review). This rendered unconditionally, so the first
                endpoint documented without a body - a GET - would have
                shown an empty black box under a Request Body heading,
                beside a code example that correctly sends nothing. The
                other half of the same fix the example builders got. */}
            {endpoint.requestBody && (
              <div className="mb-6">
                <h3 className="text-base font-semibold text-heading mb-2">
                  Request Body
                </h3>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                  <code>{JSON.stringify(endpoint.requestBody, null, 2)}</code>
                </pre>
              </div>
            )}

            {/* Response Example */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-heading mb-2">
                Response Example
              </h3>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code>{JSON.stringify(endpoint.responseExample, null, 2)}</code>
              </pre>
            </div>

            {/* Code Example */}
            <div>
              <h3 className="text-base font-semibold text-heading mb-2">
                Code Example
              </h3>
              {/* 2026-07-04 operator consistency directive : the
                  language tabs are a customer choice, so they carry an
                  explanatory sentence like every other choice control
                  in the suite. */}
              <p className="text-body mb-4">
                Select a language above the example to view a ready-to-run
                request.
              </p>
              {/*
                THE CONTROLS SIT ON THE BLOCK THEY ACT ON, ported from
                middleware-01's codehead row (operator directive,
                2026-08-21). The language buttons and the copy button
                used to sit in two different places : the languages in
                a header row above an explanatory paragraph, and the
                copy button floating over the top-right corner of the
                code itself, where it overlapped the first line. Both
                now share one head row attached to the block, which is
                what the published documentation does and what makes
                the copy button reachable by keyboard in reading order
                rather than only by pointer.
              */}
              <div className="rounded-lg overflow-hidden">
                {/* flex-wrap, LIKE THE CARD HEAD ROW ABOVE. Four
                    single-word buttons have no break opportunity, so a
                    flex item's default min-width:auto means none of
                    them can shrink : the row needs about 339px and a
                    phone gives it about 295. This wrapper carries
                    overflow-hidden for its rounded corners and .card
                    carries it too, so the excess is CLIPPED rather than
                    scrolled - and the rightmost child is Copy, which
                    was measured 94px outside the clip and unreachable
                    by pointer at every common phone width. A regression
                    against the absolutely-positioned button this
                    replaced, in the one part of the layout that could
                    not be seen rendered at the time. ml-auto still
                    right-aligns Copy when it drops to a second line,
                    and wrapping costs nothing at a width where the row
                    already fits. */}
                <div className="flex flex-wrap items-center gap-2 bg-gray-800 px-3 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.id}
                      type="button"
                      onClick={() => setSelectedLanguage(endpoint.id, lang.id)}
                      className={`px-3 py-1 rounded-md font-medium transition-colors ${
                        getSelectedLanguage(endpoint.id) === lang.id
                          ? "bg-blue-600 text-white"
                          : "text-gray-300 hover:bg-gray-700"
                      }`}
                      aria-pressed={
                        getSelectedLanguage(endpoint.id) === lang.id
                      }
                    >
                      {lang.label}
                    </button>
                  ))}
                  {/* 2026-07-05 dark-mode sweep : no dark: pair in this
                      head row ; it sits on the code block, which stays
                      dark in BOTH themes. */}
                  <button
                    type="button"
                    onClick={() =>
                      handleCopy(
                        getCodeExample(
                          endpoint,
                          getSelectedLanguage(endpoint.id)
                        ),
                        `${endpoint.id}-code`
                      )
                    }
                    className="ml-auto rounded-md border border-control px-3 py-1 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    {copyStatus[`${endpoint.id}-code`] ? "Copied" : "Copy"}
                  </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 overflow-x-auto">
                  <code>
                    {getCodeExample(endpoint, getSelectedLanguage(endpoint.id))}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error Responses */}
      <div
        id="error-responses"
        tabIndex={-1}
        className="scroll-mt-24 mt-8 card p-6"
      >
        {/* Section heading at the reference size (see the API Endpoint
            Information comment above). */}
        <h2 className="text-lg font-semibold text-heading mb-2">
          Error Responses
        </h2>
        <p className="text-body mb-4">
          Error responses use one of two formats depending on the type of error
          :
        </p>

        <h3 className="text-base font-semibold text-heading mb-2 mt-4">
          General Errors
        </h3>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
          <code>
            {JSON.stringify(
              {
                success: false,
                error: "A sentence describing what went wrong.",
              },
              null,
              2
            )}
          </code>
        </pre>

        <h3 className="text-base font-semibold text-heading mb-2 mt-4">
          Validation Errors
        </h3>
        <p className="text-body mb-4">
          Returned when request body fields fail validation (e.g. missing or
          invalid domain, method, or expected value) :
        </p>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
          <code>
            {JSON.stringify(
              {
                success: false,
                errors: [
                  {
                    type: "field",
                    msg: "A domain name is required.",
                    path: "domain",
                    location: "body",
                  },
                ],
              },
              null,
              2
            )}
          </code>
        </pre>

        <div className="mt-4 space-y-2">
          <p className="text-body">
            <span className="font-medium">400 Bad Request :</span> Invalid input
            or missing required parameters
          </p>
          <p className="text-body">
            <span className="font-medium">404 Not Found :</span> Endpoint not
            found
          </p>
          <p className="text-body">
            <span className="font-medium">429 Too Many Requests :</span> Rate
            limit exceeded
          </p>
          <p className="text-body">
            <span className="font-medium">500 Internal Server Error :</span>{" "}
            Server error occurred
          </p>
        </div>
      </div>

      {/* Integration Guide */}
      <div
        id="integration-guide"
        tabIndex={-1}
        className="scroll-mt-24 mt-8 card p-6"
      >
        {/* Section heading at the reference size (see the API Endpoint
            Information comment above), with the explanatory paragraph
            every section heading carries. */}
        <h2 className="text-lg font-semibold text-heading mb-2">
          Integration Guide
        </h2>
        <p className="text-body mb-4">
          These practical notes cover the details you need when wiring the API
          into your own application.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-body">
          <div className="space-y-4">
            <div>
              {/* Integration Guide topics as sub-headings at the
                  standard text-base font-semibold sub-heading tier
                  (see the API Endpoint Information comment above). */}
              <h3 className="text-base font-semibold text-heading mb-2">
                Authentication
              </h3>
              <p className="leading-relaxed">
                The API is currently open and does not require authentication.
                This allows for easy integration and testing without the need
                for API keys or tokens. Please note that this may change in
                future versions to ensure better security and rate limiting
                control.
              </p>
            </div>
            <div>
              <h3 className="text-base font-semibold text-heading mb-2">
                Content Type
              </h3>
              <p className="leading-relaxed">
                All requests must include the header{" "}
                <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-sm">
                  Content-Type: application/json
                </code>{" "}
                to ensure proper parsing of request bodies. The API expects all
                data to be sent in JSON format and will return responses in JSON
                format as well.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-heading mb-2">
                CORS Support
              </h3>
              <p className="leading-relaxed">
                Cross-Origin Resource Sharing (CORS) is enabled for all origins,
                allowing browser-based applications to use the API directly from
                client-side JavaScript. This makes it easy to integrate the SSL
                tools into single-page applications and web-based tools without
                needing a proxy server.
              </p>
            </div>
            <div>
              <h3 className="text-base font-semibold text-heading mb-2">
                SSL/TLS Requirements
              </h3>
              <p className="leading-relaxed">
                All API endpoints should be accessed over HTTPS in production
                environments to ensure the security of SSL Certificate data in
                transit. The API supports modern TLS protocols and cipher suites
                to maintain the highest level of security for your SSL
                Certificate operations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;
